import { useState } from "react";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { boolean, object } from "yup";

import type {
  TppConfiguration,
  UpsertTppInstanceRequest,
} from "@thalamos/aspire-common";
import { nonEmptyRequiredString } from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import {
  Button,
  FormLabel,
  HelperText,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

const tppInstanceDetailsSchema = object({
  displayName: nonEmptyRequiredString
    .default("")
    .required("Please enter a display name"),
  systemName: nonEmptyRequiredString
    .default("")
    .required("Please enter a system name"),
  aes128Key: nonEmptyRequiredString
    .default("")
    .required("Please enter the AES-128 key")
    .matches(/^[0-9a-fA-F]{32}$/, "Must be a 32-character hex string"),
  aes128Iv: nonEmptyRequiredString
    .default("")
    .required("Please enter the AES-128 IV")
    .matches(/^[0-9a-fA-F]{32}$/, "Must be a 32-character hex string"),
  odsCode: nonEmptyRequiredString
    .default("")
    .required("Please enter the ODS code"),
  rememberLoginEnabled: boolean().default(false),
});

function DeleteTppInstanceButton({
  tppInstance,
}: {
  tppInstance: TppConfiguration;
}) {
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);
  const navigate = useNavigate();
  return (
    <>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => setConfirmFn(null)}
        />
      )}

      <Button
        onClick={() => {
          setConfirmFn({
            message: `Are you sure you want to delete this TPP instance ${tppInstance.displayName}? This cannot be undone.`,
            confirmFn: async () => {
              try {
                const result = await api.tpp.instance.delete(
                  tppInstance.instanceId,
                );

                if (result.status === 204) {
                  renderSuccessToast({
                    message: `TPP instance ${tppInstance.displayName} has been deleted`,
                  });
                  navigate(routeFns.adminTppInstancesPage());
                } else {
                  renderErrorToast({
                    message: `Failed to remove TPP instance: ${tppInstance.displayName} with error: ${result.data.reason}`,
                  });
                }
              } catch (error: unknown) {
                let message = "";
                if (
                  typeof error === "object" &&
                  error !== null &&
                  "response" in error
                ) {
                  const axiosError = error as {
                    response?: { data?: { reason?: string } };
                  };
                  message = `Failed to remove TPP instance: ${tppInstance.displayName} with error: ${axiosError.response?.data?.reason ?? "Unknown error"}`;
                } else {
                  message = `Failed to remove TPP instance: ${tppInstance.displayName}`;
                }
                renderErrorToast({ message });
              }
              setConfirmFn(null);
            },
          });
        }}
        sx={{ ml: "1em" }}
        color={"error"}
        label={"Delete"}
      />
    </>
  );
}

function TppInstanceEdit({
  tppInstanceId,
  tppInstance,
  refetchTppInstance,
}: {
  tppInstanceId: string;
  tppInstance: TppConfiguration | null;
  refetchTppInstance: () => void;
}) {
  const initialData = {
    ...tppInstanceDetailsSchema.getDefault(),
    ...(tppInstance
      ? {
          displayName: tppInstance.displayName ?? "",
          systemName: tppInstance.systemName ?? "",
          aes128Key: tppInstance.aes128Key ?? "",
          aes128Iv: tppInstance.aes128Iv ?? "",
          odsCode: tppInstance.odsCode ?? "",
          rememberLoginEnabled: tppInstance.rememberLoginEnabled ?? false,
        }
      : {}),
  };

  return (
    <Formik<UpsertTppInstanceRequest>
      initialValues={initialData}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const cleanedData: UpsertTppInstanceRequest = {
          displayName: values.displayName.trim(),
          systemName: values.systemName.trim(),
          aes128Key: values.aes128Key.trim(),
          aes128Iv: values.aes128Iv.trim(),
          odsCode: values.odsCode.trim(),
          rememberLoginEnabled: values.rememberLoginEnabled ?? false,
        };
        if (tppInstance) {
          const result = await api.tpp.instance.update(
            tppInstanceId,
            cleanedData,
          );
          if (result.status === 204) {
            refetchTppInstance();
            renderSuccessToast({
              message: "TPP instance updated successfully",
            });
          } else {
            renderErrorToast({ message: `${result.data.reason}` });
          }
        } else {
          const result = await api.tpp.instance.create(
            tppInstanceId,
            cleanedData,
          );
          if (result.status === 204) {
            refetchTppInstance();
            renderSuccessToast({
              message: "TPP instance created successfully",
            });
          } else {
            renderErrorToast({ message: `${result.data.reason}` });
          }
        }
        setSubmitting(false);
      }}
      validationSchema={tppInstanceDetailsSchema}
    >
      {(formikData) => {
        const { values, setValues, errors, touched, submitForm, dirty } =
          formikData;
        return (
          <>
            <Box sx={{ maxWidth: "600px", marginBottom: "2em" }}>
              <FormLabel
                label={"Display name (*)"}
                error={touched.displayName && !!errors.displayName}
              />
              <TextField
                name={"displayName"}
                useFullWidth={true}
                value={values.displayName}
                onChange={(e) => {
                  setValues({ ...values, displayName: e });
                }}
              />
              {errors.displayName && touched.displayName && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.displayName as string} />
                </Box>
              )}

              <FormLabel
                label={"System name (*)"}
                error={touched.systemName && !!errors.systemName}
              />
              <TextField
                name={"systemName"}
                useFullWidth={true}
                value={values.systemName}
                onChange={(e) => {
                  setValues({ ...values, systemName: e });
                }}
              />
              {errors.systemName && touched.systemName && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.systemName as string} />
                </Box>
              )}

              <FormLabel
                label={"AES-128 Key (32-character hex) (*)"}
                error={touched.aes128Key && !!errors.aes128Key}
              />
              <TextField
                name={"aes128Key"}
                useFullWidth={true}
                value={values.aes128Key}
                onChange={(e) => {
                  setValues({ ...values, aes128Key: e });
                }}
              />
              {errors.aes128Key && touched.aes128Key && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.aes128Key as string} />
                </Box>
              )}

              <FormLabel
                label={"AES-128 IV (32-character hex) (*)"}
                error={touched.aes128Iv && !!errors.aes128Iv}
              />
              <TextField
                name={"aes128Iv"}
                useFullWidth={true}
                value={values.aes128Iv}
                onChange={(e) => {
                  setValues({ ...values, aes128Iv: e });
                }}
              />
              {errors.aes128Iv && touched.aes128Iv && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.aes128Iv as string} />
                </Box>
              )}

              <FormLabel
                label={"ODS Code (*)"}
                error={touched.odsCode && !!errors.odsCode}
              />
              <TextField
                name={"odsCode"}
                useFullWidth={true}
                value={values.odsCode}
                onChange={(e) => {
                  setValues({ ...values, odsCode: e });
                }}
              />
              {errors.odsCode && touched.odsCode && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.odsCode as string} />
                </Box>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.rememberLoginEnabled ?? false}
                    onChange={() => {
                      void setValues({
                        ...values,
                        rememberLoginEnabled: !values.rememberLoginEnabled,
                      });
                    }}
                    data-testid="remember-login-enabled"
                  />
                }
                label={"Remember clinician sign-in (pre-fill Auth0 login hint)"}
              />
            </Box>

            <Box sx={{ mb: 2, mt: 1 }}>
              <Button
                label={tppInstance ? "Update" : "Create"}
                onClick={submitForm}
                disabled={!dirty}
              />
              {tppInstance && (
                <DeleteTppInstanceButton tppInstance={tppInstance} />
              )}
            </Box>
          </>
        );
      }}
    </Formik>
  );
}

function TppInstanceEditPageInner({
  tppInstanceId,
  tppInstance,
  refetchTppInstance,
}: {
  tppInstanceId: string;
  tppInstance: TppConfiguration | null;
  refetchTppInstance: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {tppInstance ? (
        <h2>{tppInstance.displayName}</h2>
      ) : (
        <h2>Create New TPP Instance</h2>
      )}
      <TppInstanceEdit
        tppInstanceId={tppInstanceId}
        tppInstance={tppInstance}
        refetchTppInstance={refetchTppInstance}
      />
    </Box>
  );
}

export function TppInstanceCreateEdit() {
  const { tppInstanceId } = useParams();
  const navigate = useNavigate();
  const [
    { data: tppInstance, loading: tppInstanceLoading, response },
    refetchTppInstance,
  ] = apiHooks.tpp.get(tppInstanceId!);

  return tppInstanceLoading ? (
    <></>
  ) : (
    <>
      <TppInstanceEditPageInner
        tppInstanceId={tppInstanceId!}
        tppInstance={response?.status === 200 ? tppInstance! : null}
        refetchTppInstance={refetchTppInstance}
      />
      <Box sx={{ mb: 2, mt: 1 }}>
        <Button
          label={"Back"}
          onClick={() => navigate(routeFns.adminTppInstancesPage())}
        />
      </Box>
    </>
  );
}
